/**
 * React-hook-form registration for a money field, with the typing rules the
 * shared numeric kit already defines.
 *
 * The perps price and size fields were registered raw, so the field accepted
 * whatever the browser would hold:
 *  - `70000,55` in the limit price became **7000055.00** with the CTA enabled --
 *    a 100x order. (The field also asked for `type="number"`, which makes Chrome
 *    eat the comma before any handler sees it, so that had to go too; the shared
 *    `Input` already defaults to `text` for exactly this reason.)
 *  - `1e3` in the size field became a **$1,000 order**, `-5` rendered an Order
 *    Value of **-$5.00**, and `abc` / `1.2.3` sat in the field as typed.
 *
 * `sanitizeTyping` is deliberately used on every change rather than a blur-time
 * reformat: it strips what cannot belong in a number and otherwise leaves the
 * text alone, so the caret does not jump while the trader is still typing.
 *
 * Paste goes through `normalizePaste` instead, because the two rules differ.
 * `sanitizeTyping` replaces the FIRST comma with a dot (right for the European
 * decimal `70000,55`) which would mangle a pasted `1,234.56` into `1.23456`;
 * `normalizePaste` treats the LAST separator as the decimal point and handles
 * both conventions.
 */
import { ClipboardEvent } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { normalizePaste, sanitizeTyping } from "../../../utils/amount/sanitize";

/**
 * Generous on purpose. This guard exists to reject what is not a number at all;
 * how many decimals a particular market accepts is already decided downstream by
 * the form's own validators and by the step-flooring on submit. Picking a
 * tighter number here would quietly become a second, competing source of truth.
 * Perp prices and sizes are 1e18 across every chain.
 */
export const MAX_INPUT_DECIMALS = 18;

export const sanitizedRegister = (
  register: UseFormRegisterReturn
): UseFormRegisterReturn & {
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
} => ({
  ...register,
  // Typed as react-hook-form's own `ChangeHandler` (via the spread), whose
  // event is structural -- `{ target, type }` -- not a React ChangeEvent.
  onChange: (event: { target: { value?: string } }) => {
    const el = event.target;
    if (typeof el?.value === "string") {
      const clean = sanitizeTyping(el.value, MAX_INPUT_DECIMALS);
      // Only touch the DOM when something was actually rejected: assigning
      // `value` moves the caret to the end, which would fight anyone editing
      // the middle of what they had already typed.
      if (clean !== el.value) el.value = clean;
    }
    return register.onChange(event);
  },
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData?.getData("text");
    if (pasted === undefined) return;
    const normalized = normalizePaste(pasted, MAX_INPUT_DECIMALS);
    event.preventDefault();
    // Unparseable paste is dropped rather than partially applied: half of a
    // mistyped number is a worse starting point than the field as it was.
    if (normalized === null) return;
    const el = event.currentTarget;
    el.value = normalized;
    register.onChange({ target: el, type: "change" });
  },
});
